import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react'
import {LayoutSplashScreen} from '../../_metronic/layout/core'
import {WithChildren} from '../../_metronic/helpers'
import { AuthModel } from '../models/auth/auth.model'
import { UserModel } from '../models/user/user.model'
import { logoff } from '../services/auth.service'
import {
  getAuth,
  setAuth,
  removeAuth,
  getSessionExpiredMessage,
  setSessionExpiredMessage,
  removeSessionExpiredMessage,
  isTokenExpired,
} from '../utils/auth.utils'
import { getUserDetails } from '../services/user.service'
import { useInactivityTimer } from '../utils/useInactivityTimer.utils'

const SESSION_EXPIRED_INACTIVITY_MESSAGE =
  'Your session has expired due to inactivity. Please log in again.'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  logout: () => void
  sessionExpiredMessage: string | undefined
  logoutDueToInactivity: () => void
}

const initAuthContextPropsState = {
  auth: getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
  sessionExpiredMessage: undefined,
  logoutDueToInactivity: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth2] = useState<AuthModel | undefined>(getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()
  const [sessionExpiredMessage, setSessionExpiredMessageState] = useState<string | undefined>(
    () => {
      const message = getSessionExpiredMessage()
      removeSessionExpiredMessage()
      return message
    }
  )

  const saveAuth = (newAuth: AuthModel | undefined) => {
    setAuth2(newAuth)
    if (newAuth) {
      setAuth(newAuth)
      setSessionExpiredMessageState(undefined)
    } else {
      removeAuth()
    }
  }

  const logout = () => {
    saveAuth(undefined)
    setCurrentUser(undefined)
    logoff();
  }

  const logoutDueToInactivity = () => {
    setSessionExpiredMessage(SESSION_EXPIRED_INACTIVITY_MESSAGE)
    setSessionExpiredMessageState(SESSION_EXPIRED_INACTIVITY_MESSAGE)
    saveAuth(undefined)
    setCurrentUser(undefined)
    logoff()
    window.location.href = `${process.env.PUBLIC_URL || ''}/auth`
  }

  useInactivityTimer(logoutDueToInactivity, !!auth)

  useEffect(() => {
    const handleActivity = () => {
      const storedAuth = getAuth()
      if (storedAuth && storedAuth.api_token && isTokenExpired(storedAuth)) {
        logoutDueToInactivity()
      }
    }

    document.addEventListener('click', handleActivity)
    document.addEventListener('keydown', handleActivity)
    document.addEventListener('touchstart', handleActivity)

    return () => {
      document.removeEventListener('click', handleActivity)
      document.removeEventListener('keydown', handleActivity)
      document.removeEventListener('touchstart', handleActivity)
    }
  }, [])

  return (
    <AuthContext.Provider
      value={{
        auth,
        saveAuth,
        currentUser,
        setCurrentUser,
        logout,
        sessionExpiredMessage,
        logoutDueToInactivity,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {auth, currentUser, setCurrentUser, saveAuth} = useAuth()
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  useEffect(() => {
    const requestUser = async () => {
      try {
        if (!didRequest.current) {
          const storedAuth = getAuth();
          if(storedAuth) {
            const currentDate = new Date().getTime();

            if(storedAuth.expiresAt && currentDate >= storedAuth.expiresAt) {
              saveAuth(undefined);
              setCurrentUser(undefined);
            } else {
              const user:UserModel = {
                organization_primaryEmail: storedAuth.email,
                token: storedAuth.api_token
              }
              setCurrentUser(user);

              getUserDetails(storedAuth.email).then((data) => {
                setCurrentUser(data);
             });

            }
          }
        }
      } catch (error) {
        console.error(error)
      } finally {
        setShowSplashScreen(false)
      }

      return () => (didRequest.current = true)
    }

    if (auth && auth.api_token) {
      requestUser()
    } else {
      setShowSplashScreen(false)
    }
  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthProvider, AuthInit, useAuth}
